import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Badge,
  Spinner,
  Box,
  Divider,
  List,
  Icon,
  InlineGrid,
  InlineStack,
  BlockStack,
  ProgressBar,
  Toast,
  Frame
} from '@shopify/polaris';
import {
  CreditCardMajor,
  CircleTickMajor,
  OrdersMajor,
  EmailMajor,
  SettingsMajor
} from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';
import { useShopifyNavigation } from '../hooks/useShopifyNavigation';
import api from '../utils/axiosConfig';

function Billing() {
  const { t } = useTranslation();
  const navigateWithContext = useShopifyNavigation();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);
  const [billingInterval, setBillingInterval] = useState('monthly'); // Added billing interval state

  useEffect(() => {
    // Check for confirmation status in URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const message = urlParams.get('message');
    const chargeId = urlParams.get('charge_id');
    
    if (status) {
      handleConfirmationStatus(status, message, chargeId);
      // Clean up URL parameters
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    
    // Wait a bit for App Bridge to initialize and URL to be fixed
    const timer = setTimeout(() => {
      loadBillingData();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const handleConfirmationStatus = (status, message, chargeId) => {
    switch (status) {
      case 'success':
        const successMsg = message ? decodeURIComponent(message) : 'Subscription confirmed - charges will appear on your Shopify bill!';
        setToastMessage(`✅ ${successMsg}`);
        setToastError(false);
        setToastActive(true);
        console.log('🎉 Billing confirmation successful:', {
          status,
          chargeId,
          message: successMsg,
          billingNote: 'Charges will be added to merchant\'s Shopify bill monthly'
        });
        // Automatically refresh billing data after success
        setTimeout(() => {
          loadBillingData();
        }, 1000);
        break;
      case 'error':
        const errorMsg = message ? decodeURIComponent(message) : 'Unknown error';
        setToastMessage(`❌ Subscription failed: ${errorMsg}`);
        setToastError(true);
        setToastActive(true);
        console.error('💸 Billing confirmation failed:', { status, message: errorMsg, chargeId });
        break;
      case 'unknown':
        const unknownMsg = message ? decodeURIComponent(message) : 'Subscription status unknown. Please check your billing information.';
        setToastMessage(`⚠️ ${unknownMsg}`);
        setToastError(true);
        setToastActive(true);
        console.warn('❓ Billing confirmation unclear:', { status, message: unknownMsg, chargeId });
        break;
      default:
        console.log('Unknown confirmation status:', status);
    }
  };

  const loadBillingData = async () => {
    try {
      setLoading(true);
      
      const [plansRes, subscriptionRes, usageRes] = await Promise.all([
        api.get('/billing/plans'),
        api.get('/billing/subscription'),
        api.get('/billing/usage')
      ]);
      
      setPlans(plansRes.data);
      setCurrentSubscription(subscriptionRes.data.subscription);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribe = async (planId) => {
    try {
      // Construct return URL that will be handled by our confirmation callback
      const baseUrl = window.location.origin;
      const returnUrl = `${baseUrl}/api/billing/confirm`;
      
      console.log('Starting subscription for plan:', planId, 'with billing interval:', billingInterval, 'and returnUrl:', returnUrl);
      
      const response = await api.post('/billing/subscribe', {
        planId,
        billingInterval, // Pass the selected billing interval
        returnUrl
      });
      
      console.log('Subscription response:', response.data);
      
      // Check if this is a managed pricing app
      if (response.data.managedPricing) {
        // For managed pricing apps, just reload the page to show updated subscription
        setToastMessage('✅ Subscription updated successfully!');
        setToastError(false);
        setToastActive(true);
        loadBillingData();
      } else if (response.data.confirmationUrl) {
        // Redirect to Shopify billing confirmation for regular billing
        console.log('Redirecting to confirmation URL:', response.data.confirmationUrl);
        window.top.location.href = response.data.confirmationUrl;
      } else {
        throw new Error('No confirmation URL received from server');
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      
      // Check if this is an authentication error requiring reinstall
      if (error.response?.data?.requiresReinstall) {
        setToastMessage('🔐 App needs to be reinstalled to complete authentication. Please uninstall and reinstall the app.');
        setToastError(true);
        setToastActive(true);
        return;
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create subscription';
      setToastMessage(`❌ Subscription error: ${errorMessage}`);
      setToastError(true);
      setToastActive(true);
    }
  };

  const cancelSubscription = async () => {
    if (!confirm(t('billing.confirmCancel'))) {
      return;
    }
    
    try {
      await api.post('/billing/cancel');
      loadBillingData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      alert(t('billing.cancelError'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getSubscriptionBadge = (status) => {
    const statusMap = {
      active: { status: 'success', content: t('billing.subscriptionStatus.active') },
      trialing: { status: 'info', content: t('billing.subscriptionStatus.trialing') },
      cancelled: { status: 'critical', content: t('billing.subscriptionStatus.cancelled') },
      past_due: { status: 'warning', content: t('billing.subscriptionStatus.past_due') }
    };
    
    return statusMap[status] || { status: 'new', content: status };
  };

  if (loading) {
    return (
      <Page title={t('billing.title')}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p" color="subdued">
                  {t('billing.loadingBillingInfo')}
                </Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const toastMarkup = toastActive ? (
    <Toast
      content={toastMessage}
      error={toastError}
      onDismiss={() => setToastActive(false)}
      duration={6000}
    />
  ) : null;

  return (
    <>
      {toastMarkup}
      <Page
        title={t('billing.title')}
        subtitle={t('billing.subtitle')}
      >
        <Layout>
        {/* Current Subscription - Enhanced Design */}
        {currentSubscription && (
          <Layout.Section>
            <Card padding="0" sectioned={false}>
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '32px',
                color: 'white',
                borderRadius: '12px 12px 0 0'
              }}>
                <InlineStack gap="400" align="space-between" blockAlign="start">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.2)', 
                      padding: '16px', 
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backdropFilter: 'blur(10px)'
                    }}>
                      <Icon source={CreditCardMajor} color="base" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingLg" as="h2" color="base">
{t('billing.currentPlanTitle')}: {currentSubscription.plan.name}
                      </Text>
                      <InlineStack gap="300" align="start" wrap={false}>
                        <div style={{
                          backgroundColor: currentSubscription.status === 'active' ? 'rgba(0, 160, 71, 0.9)' : 'rgba(255, 255, 255, 0.2)',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}>
{currentSubscription.status === 'active' ? `✓ ${t('billing.activeStatus')}` : currentSubscription.status}
                        </div>
                        <Text variant="bodyLg" as="p" color="base">
{currentSubscription.plan.price === 0 ? t('billing.free') : `$${currentSubscription.plan.price}${t('billing.perMonth')}`}
                        </Text>
                      </InlineStack>
                      {currentSubscription.currentPeriodEnd && (
                        <Text variant="bodySm" as="p" color="base" opacity="0.9">
{t('billing.nextBillingDate')}: {formatDate(currentSubscription.currentPeriodEnd)}
                        </Text>
                      )}
                    </BlockStack>
                  </InlineStack>
                  
                  {currentSubscription.status === 'active' && currentSubscription.plan.price > 0 && (
                    <Button 
                      outline 
                      destructive 
                      onClick={cancelSubscription}
                      size="large"
                    >
                      {t('billing.cancelPlan')}
                    </Button>
                  )}
                </InlineStack>
              </div>
              
              <Box padding="400">
                <InlineGrid columns={{ xs: '1', sm: '3' }} gap="400">
                  {/* Usage Stats */}
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '12px',
                    border: '1px solid #e1e3e5'
                  }}>
                    <Text variant="heading2xl" as="h3" color="success">
                      {currentSubscription.currentTrackingCount}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('billing.ordersProcessed')}
                    </Text>
                  </div>
                  
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '12px',
                    border: '1px solid #e1e3e5'
                  }}>
                    <Text variant="heading2xl" as="h3" color="primary">
                      {currentSubscription.plan.trackingLimit}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('billing.monthlyLimit')}
                    </Text>
                  </div>
                  
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: currentSubscription.remainingTracks < 5 ? '#fef2f2' : '#f0fdf4',
                    borderRadius: '12px',
                    border: '1px solid #e1e3e5'
                  }}>
                    <Text variant="heading2xl" as="h3" color={currentSubscription.remainingTracks < 5 ? 'critical' : 'success'}>
                      {currentSubscription.remainingTracks || 0}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('billing.ordersRemaining')}
                    </Text>
                  </div>
                </InlineGrid>
                
                {/* Progress Bar */}
                <Box paddingBlockStart="400">
                  <Text variant="bodyMd" as="p" fontWeight="semibold">
                    {t('billing.currentUsage')}: {Math.round((currentSubscription.currentTrackingCount / currentSubscription.plan.trackingLimit) * 100)}%
                  </Text>
                  <Box paddingBlockStart="200">
                    <ProgressBar 
                      progress={Math.round((currentSubscription.currentTrackingCount / currentSubscription.plan.trackingLimit) * 100)}
                      size="large"
                      color={currentSubscription.remainingTracks < 5 ? 'critical' : 'primary'}
                    />
                  </Box>
                  <Box paddingBlockStart="200">
                    <Text variant="bodySm" as="p" color="subdued">
                      {currentSubscription.remainingTracks < 5 
                        ? `⚠️ ${t('billing.nearLimitWarning')}` 
                        : `✅ ${t('billing.plentySpaceMessage')}`}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Available Plans - Enhanced Modern Design */}
        <Layout.Section>
            <Box paddingBlockEnd="600">
              <Text variant="headingLg" as="h2" alignment="center">
                {t('billing.choosePerfectPlan')}
              </Text>
              <Box paddingBlockStart="200">
                <Text variant="bodyLg" as="p" color="subdued" alignment="center">
                  {t('billing.simplePricing')}
                </Text>
              </Box>
              
              {/* Billing Interval Toggle */}
              <Box paddingBlockStart="400">
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '16px'
                }}>
                  <Text variant="bodyMd" as="span" color={billingInterval === 'monthly' ? 'default' : 'subdued'}>
                    {t('billing.monthly') || 'Monthly'}
                  </Text>
                  <div style={{
                    position: 'relative',
                    width: '56px',
                    height: '28px',
                    backgroundColor: billingInterval === 'annual' ? '#00a047' : '#e1e3e5',
                    borderRadius: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }} onClick={() => setBillingInterval(billingInterval === 'monthly' ? 'annual' : 'monthly')}>
                    <div style={{
                      position: 'absolute',
                      top: '2px',
                      left: billingInterval === 'annual' ? '30px' : '2px',
                      width: '24px',
                      height: '24px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }} />
                  </div>
                  <Text variant="bodyMd" as="span" color={billingInterval === 'annual' ? 'default' : 'subdued'}>
                    {t('billing.annual') || 'Annual'} 
                    <span style={{ 
                      color: '#00a047', 
                      fontWeight: '600', 
                      marginLeft: '4px' 
                    }}>
                      ({t('billing.save20') || 'Save 20%'})
                    </span>
                  </Text>
                </div>
              </Box>
            </Box>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'stretch',
              flexWrap: 'wrap',
              gap: '24px'
            }}>
            {plans
              .filter(plan => plan.billingPeriod === billingInterval)
              .map((plan, index) => {
              const isPopular = plan.name === 'Premium';
              const isPremium = plan.price >= 19;
              const isCurrentPlan = currentSubscription?.plan.id === plan.id;
              
              const planStyles = {
                Free: { 
                  gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  bgColor: '#f8fafc',
                  iconBg: '#e2e8f0',
                  iconColor: 'subdued'
                },
                Starter: { 
                  gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  bgColor: '#f0fdf4',
                  iconBg: '#dcfce7',
                  iconColor: 'success'
                },
                Premium: { 
                  gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  bgColor: '#eff6ff',
                  iconBg: '#dbeafe',
                  iconColor: 'primary'
                },
                Scaler: {
                  gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  bgColor: '#faf5ff',
                  iconBg: '#e9d5ff',
                  iconColor: 'magic'
                }
              };
              
              const style = planStyles[plan.name] || planStyles.Free;
              
              return (
                <div key={plan.id} style={{ 
                  position: 'relative', 
                  flex: '1 1 280px', 
                  maxWidth: '320px',
                  minWidth: '280px'
                }}>
                  {isPopular && (
                    <div style={{
                      position: 'absolute',
                      top: '-12px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      color: 'white',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '600',
                      zIndex: 10,
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}>
                      ⭐ {t('billing.mostPopular')}
                    </div>
                  )}
                  
                  <Card 
                    padding="0"
                    background={style.bgColor}
                    sectioned={false}
                  >
                    <div style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: isCurrentPlan ? '2px solid #00a047' : isPopular ? '2px solid #3b82f6' : '1px solid #e1e3e5',
                      transition: 'all 0.3s ease',
                      transform: isPopular ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: isPopular ? '0 8px 25px rgba(59, 130, 246, 0.15)' : '0 2px 8px rgba(0, 0, 0, 0.06)'
                    }}>
                      {/* Header with Gradient */}
                      <div style={{
                        background: style.gradient,
                        padding: '24px 24px 16px',
                        color: 'white',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <Icon 
                            source={plan.name === 'Free' ? CircleTickMajor : plan.name === 'Starter' ? OrdersMajor : plan.name === 'Premium' ? SettingsMajor : EmailMajor} 
                            color="base" 
                          />
                        </div>
                        
                        <Text variant="headingMd" as="h3" color="base">
                          {plan.name}
                        </Text>
                        
                        <div style={{ margin: '8px 0' }}>
                          {plan.price === 0 ? (
                            <Text variant="heading2xl" as="h2" color="base">
                              {t('billing.free')}
                            </Text>
                          ) : billingInterval === 'monthly' ? (
                            <>
                              <Text variant="heading2xl" as="h2" color="base">
                                ${plan.price}
                              </Text>
                              <Text variant="bodySm" as="span" color="base" opacity="0.8">
                                {t('billing.perMonth')}
                              </Text>
                            </>
                          ) : (
                            <>
                              <Text variant="heading2xl" as="h2" color="base">
                                ${plan.billingPeriod === 'annual' ? plan.price.toFixed(2) : (plan.price * 12 * 0.8).toFixed(2)}
                              </Text>
                              <Text variant="bodySm" as="span" color="base" opacity="0.8">
                                {t('billing.perYear') || '/year'}
                              </Text>
                              <div style={{ marginTop: '4px' }}>
                                <Text variant="bodySm" as="span" color="base" opacity="0.6" style={{ textDecoration: 'line-through' }}>
                                  ${plan.billingPeriod === 'annual' ? (plan.price / 0.8).toFixed(2) : (plan.price * 12).toFixed(2)}
                                </Text>
                              </div>
                            </>
                          )}
                        </div>
                        {/* Add 7-day trial badge for paid plans */}
                        {plan.price > 0 && (
                          <div style={{
                            marginTop: '8px',
                            padding: '4px 8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: '12px',
                            display: 'inline-block'
                          }}>
                            <Text variant="bodySm" as="span" color="base" fontWeight="semibold">
                              🎉 {t('billing.trial7Days') || '7-day free trial'}
                            </Text>
                          </div>
                        )}
                      </div>
                      
                      {/* Content */}
                      <Box padding="400">
                        <BlockStack gap="300">
                          <Text variant="bodyMd" as="p" color="subdued" alignment="center">
                            {plan.name === 'Free' && t('billing.planDescriptions.free')}
                            {plan.name === 'Starter' && t('billing.planDescriptions.starter')}
                            {plan.name === 'Premium' && t('billing.planDescriptions.premium')}
                            {plan.name === 'Scaler' && t('billing.planDescriptions.scaler')}
                          </Text>
                          
                          <Divider />
                          
                          <List spacing="tight">
                            {plan.features.slice(0, 4).map((feature, featureIndex) => (
                              <List.Item key={featureIndex}>
                                <InlineStack gap="200" align="start">
                                  <div style={{ color: style.gradient.includes('#3b82f6') ? '#3b82f6' : style.gradient.includes('#11998e') ? '#11998e' : '#00a047', marginTop: '2px' }}>✓</div>
                                  <Text variant="bodySm" as="span">{feature}</Text>
                                </InlineStack>
                              </List.Item>
                            ))}
                          </List>
                          
                          {plan.features.length > 4 && (
                            <Text variant="bodySm" as="p" color="subdued" alignment="center">
                              +{plan.features.length - 4} {t('billing.additionalResources')}
                            </Text>
                          )}
                        </BlockStack>
                        
                        <Box paddingBlockStart="400">
                          {isCurrentPlan ? (
                            <Button fullWidth disabled>
                              <InlineStack gap="200" align="center">
                                <Icon source={CircleTickMajor} color="success" />
                                <Text>{t('billing.currentPlan')}</Text>
                              </InlineStack>
                            </Button>
                          ) : (
                            <Button 
                              primary={isPopular}
                              fullWidth
                              size="large"
                              onClick={() => subscribe(plan.id)}
                              tone={isPremium ? 'critical' : isPopular ? 'success' : 'primary'}
                            >
                              {plan.price === 0 ? t('billing.startFree') : t('billing.choosePlan')}
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </div>
                  </Card>
                </div>
              );
            })}
            </div>
            
            {/* Trust Indicators */}
            <Box paddingBlockStart="600">
              <Card padding="400" background="bg-surface-secondary">
                <InlineStack gap="400" align="center" blockAlign="center">
                  <InlineStack gap="200" align="center">
                    <Icon source={CircleTickMajor} color="success" />
                    <Text variant="bodySm" color="subdued">{t('billing.cancelAnytime')}</Text>
                  </InlineStack>
                  <InlineStack gap="200" align="center">
                    <Icon source={CreditCardMajor} color="primary" />
                    <Text variant="bodySm" color="subdued">{t('billing.shopifyBilling')}</Text>
                  </InlineStack>
                  <InlineStack gap="200" align="center">
                    <Icon source={SettingsMajor} color="highlight" />
                    <Text variant="bodySm" color="subdued">{t('billing.support247')}</Text>
                  </InlineStack>
                </InlineStack>
              </Card>
            </Box>
        </Layout.Section>

        {/* Additional Stats */}
        {usage && !currentSubscription && (
          <Layout.Section>
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px' }}>
              <Card padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">
                  {t('billing.usageStats')}
                </Text>
                <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                  <div style={{
                    textAlign: 'center',
                    padding: '24px',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '12px',
                    border: '1px solid #dcfce7'
                  }}>
                    <div style={{ 
                      backgroundColor: '#22c55e', 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px'
                    }}>
                      <Icon source={EmailMajor} color="base" />
                    </div>
                    <Text variant="heading2xl" as="h3" color="success">
                      {usage.currentMonthUsage}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('billing.ordersThisMonth')}
                    </Text>
                  </div>
                  
                  <div style={{
                    textAlign: 'center',
                    padding: '24px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '12px',
                    border: '1px solid #dbeafe'
                  }}>
                    <div style={{ 
                      backgroundColor: '#3b82f6', 
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px'
                    }}>
                      <Icon source={SettingsMajor} color="base" />
                    </div>
                    <Text variant="headingMd" as="h3" color="primary">
                      {t('billing.currentPeriod')}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {formatDate(usage.period.start)} - {formatDate(usage.period.end)}
                    </Text>
                  </div>
                </InlineGrid>
              </BlockStack>
            </Card>
            </div>
          </Layout.Section>
        )}
      </Layout>
    </Page>
    </>
  );
}

export default Billing;